import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid2,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import axiosInstance from "../../../../api/axiosConfig";
import StatCard from "./components/StatCards";

const cacheRtl = createCache({
  key: "adapter-date-fns-jalali-demo",
  stylisPlugins: [prefixer, rtlPlugin],
});

function generateChartData() {
  const days = [
    "1403/11/08",
    "1403/11/09",
    "1403/11/10",
    "1403/11/11",
    "1403/11/12",
    "1403/11/13",
    "1403/11/14",
  ];
  const sessionsData = [8, 3, 5, 3, 10, 2, 4];
  const userInteractionsData = [16, 8, 13, 16, 13, 16, 15];
  const difficultyData = [2, 4, 2, 2, 5, 1, 2];
  return {
    days,
    sessionsData,
    userInteractionsData,
    difficultyData,
  };
}

function ViewCamp() {
  const [camps, setCamps] = useState([]);
  const [selectedCamp, setSelectedCamp] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chartData, setChartData] = useState({
    days: [],
    sessionsData: [],
    userInteractionsData: [],
    difficultyData: [],
  });

  const fetchChartData = () => {
    const data = generateChartData(); // Generate mock data
    setChartData(data); // Update the chart data
  };

  // Fetch camps data
  useEffect(() => {
    const fetchCamps = async () => {
      try {
        const response = await axiosInstance.get(
          "/Training/api/Training/GetTrainingCamp",
        );
        const formattedCamps = response.data.map((camp) => ({
          id: camp.trainigCampId,
          title: `${camp.campTitle} از تاریخ ${camp.startDate} الی ${camp.endDate}`,
        }));
        setCamps(formattedCamps);

        // Set the first camp as the default if no camp is selected
        if (formattedCamps.length > 0 && !selectedCamp) {
          setSelectedCamp(formattedCamps[0].id); // Set first camp as default
          fetchChartData(formattedCamps[0].id); // Fetch chart data for the first camp
        }
      } catch (err) {
        if (err.response?.data?.message) {
          setError(`خطا: ${err.response.data.message}`);
        } else {
          setError("خطا در دریافت لیست اردوها");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCamps();
  }, [selectedCamp]);

  // Fetch data based on selected camp
  const handleCampChange = (campId) => {
    setSelectedCamp(campId);
    fetchChartData(campId);
  };

  if (loading) {
    return (
      <CircularProgress sx={{ display: "block", margin: "0 auto", mt: 4 }} />
    );
  }

  return (
    <Box>
      {/* Camp Selection Dropdown */}
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          backgroundColor: "#fff",
          marginBottom: 3,
          padding: 2,
        }}
      >
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 2 }}>
          لیست اردوها
        </Typography>
        <CacheProvider value={cacheRtl}>
          <div dir="rtl">
            <FormControl fullWidth variant="outlined">
              <InputLabel id="camp-select-label">انتخاب اردو</InputLabel>
              <Select
                labelId="camp-select-label"
                value={selectedCamp}
                onChange={(e) => handleCampChange(e.target.value)}
                label="انتخاب اردو"
                dir="rtl"
              >
                {camps.map((camp) => (
                  <MenuItem key={camp.id} value={camp.id} dir="rtl">
                    {camp.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </CacheProvider>
      </Box>

      {/* Camp Stats Section */}
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          backgroundColor: "#fff",
          padding: 2,
        }}
      >
        <Grid2 container spacing={5}>
          {/* Sessions per day chart */}
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatCard
              title="تعداد تمرین در یک روز"
              value="5"
              interval="میانگین"
              trend="up"
              data={chartData.sessionsData}
              xLine={chartData.days}
            />
          </Grid2>

          {/* User interactions per day chart */}
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatCard
              title="تعداد کاربران"
              value="12"
              interval="میانگین"
              trend="neutral"
              data={chartData.userInteractionsData}
              xLine={chartData.days}
            />
          </Grid2>

          {/* Difficulty level per day chart */}
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatCard
              title="درجه سختی تمرین های روزانه"
              value="3.5"
              interval="میانگین"
              trend="down"
              data={chartData.difficultyData}
              xLine={chartData.days}
            />
          </Grid2>

          {/* Suggestion Box */}
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              پیشنهادات
            </Typography>
            <Alert severity="error" sx={{ marginTop: "10px" }}>
              <AlertTitle marginRight={2}>خطر مصدومیت !</AlertTitle>
              در روز 1403/11/12 فشار تمرین بسیار بالا بوده و احتمال مصدومیت وجود
              دارد
            </Alert>
            <Alert severity="warning" sx={{ marginTop: "10px" }}>
              <AlertTitle marginRight={2}>هشدار !</AlertTitle>
              در روز 1403/11/09 تعداد افراد شرکت کننده بسیار از میانگین پایین تر
              است و باید بررسی شود
            </Alert>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
}

export default ViewCamp;
